/**
 * apps/app/lib/purchases.web.ts
 *
 * RevenueCat wrapper for web using @revenuecat/purchases-js.
 * Metro/webpack resolves this file on web; purchases.native.ts is used on iOS/Android.
 */

import type { Package, Purchases as PurchasesClient } from '@revenuecat/purchases-js';

type PurchasesModule = typeof import('@revenuecat/purchases-js');

const WEB_KEY = process.env['EXPO_PUBLIC_REVENUECAT_WEB_KEY'] ?? '';
const REVENUECAT_ENTITLEMENT_ID = 'Per Diem Subscription';

let purchasesClient: PurchasesClient | null = null;
let configuredAppUserId: string | null = null;
let purchasesModulePromise: Promise<PurchasesModule> | null = null;
let configurePromise: Promise<void> | null = null;

// Cache raw packages so purchase() can look them up by id.
let packageCache = new Map<string, Package>();

export interface RCPackage {
  id: string;
  packageType: string;
  priceString: string;
  freeTrial: {
    duration: number;
    unit: 'day' | 'week' | 'month' | 'year';
  } | null;
  /** Discounted introductory phase that follows the trial (e.g. $1.99/month for 2 months). */
  introPrice: {
    priceString: string;
    periodUnit: 'day' | 'week' | 'month' | 'year';
    totalDuration: number;
  } | null;
}

type PackageOptions = {
  offeringIdentifier?: string;
};

type EntitlementStatus = {
  isActive: boolean;
  expiresAt: string | null;
};

type EntitlementWaitOptions = {
  attempts?: number;
  delayMs?: number;
  forceRefresh?: boolean;
};

function loadPurchasesModule(): Promise<PurchasesModule> {
  purchasesModulePromise ??= import('@revenuecat/purchases-js');
  return purchasesModulePromise;
}

export function isUserCancelledError(e: unknown): boolean {
  // PurchasesError carries a numeric code: ErrorCode.UserCancelledError === 1.
  // Checked structurally instead of importing the enum so the SDK stays
  // lazy-loaded out of the initial bundle.
  if (e && typeof e === 'object' && (e as { errorCode?: unknown }).errorCode === 1) {
    return true;
  }

  const objectValues = e && typeof e === 'object'
    ? Object.values(e as Record<string, unknown>)
      .filter((value) => typeof value === 'string')
      .join(' ')
    : '';

  if (
    objectValues.includes('UserCancelled') ||
    objectValues.includes('userCancelled') ||
    objectValues.includes('PURCHASE_CANCELLED')
  ) {
    return true;
  }

  const msg = e instanceof Error ? e.message : String(e);
  return msg.includes('UserCancelledError') || msg.includes('userCancelled') || msg.includes('PURCHASE_CANCELLED');
}

export function configureRevenueCat(userId: string): void {
  if (configuredAppUserId === userId) return;

  configuredAppUserId = userId;
  configurePromise = loadPurchasesModule()
    .then(({ Purchases }) => {
      purchasesClient = Purchases.configure(WEB_KEY, userId);
      if (__DEV__) {
        console.log('[RC] configured, key prefix:', WEB_KEY.slice(0, 10));
      }
    })
    .catch((e) => {
      purchasesClient = null;
      configuredAppUserId = null;
      console.error('[RC] configure failed:', e);
    });
}

async function getClient(): Promise<PurchasesClient> {
  if (purchasesClient) return purchasesClient;

  if (configurePromise) {
    await configurePromise;
  }

  if (purchasesClient) return purchasesClient;
  throw new Error('RevenueCat not configured');
}

async function getCustomerInfo() {
  return (await getClient()).getCustomerInfo();
}

export async function getPackages(options: PackageOptions = {}): Promise<RCPackage[]> {
  const offerings = await (await getClient()).getOfferings();
  const offering = options.offeringIdentifier
    ? offerings.all[options.offeringIdentifier]
    : offerings.current;
  if (!offering) return [];

  packageCache.clear();
  return offering.availablePackages.map((pkg) => {
    packageCache.set(pkg.identifier, pkg);
    const freeTrialPeriod = pkg.rcBillingProduct.freeTrialPhase?.period ?? null;
    const introPhase = pkg.rcBillingProduct.introPricePhase;
    return {
      id: pkg.identifier,
      // Normalize to match native SDK: '$rc_monthly' -> 'MONTHLY'
      packageType: pkg.packageType.replace('$rc_', '').toUpperCase(),
      priceString: pkg.rcBillingProduct.currentPrice.formattedPrice,
      freeTrial: freeTrialPeriod
        ? { duration: freeTrialPeriod.number, unit: freeTrialPeriod.unit }
        : null,
      introPrice: introPhase?.price && introPhase.period
        ? {
            priceString: introPhase.price.formattedPrice,
            periodUnit: introPhase.period.unit,
            totalDuration: introPhase.period.number * Math.max(introPhase.cycleCount, 1),
          }
        : null,
    };
  });
}

// The SDK mounts its checkout into this element on the host page.
const CHECKOUT_UI_ROOT_ID = 'rcb-ui-root';
const CHECKOUT_POLL_INTERVAL_MS = 400;
// After the checkout UI disappears, wait this long for the purchase promise to
// settle on its own (successful purchases briefly unmount before resolving)
// before treating the close as an abandoned purchase.
const CHECKOUT_DISMISSAL_GRACE_MS = 2_000;

function isCheckoutUiMounted(): boolean {
  if (typeof document === 'undefined') return false;
  const root = document.getElementById(CHECKOUT_UI_ROOT_ID);
  return !!root && root.childElementCount > 0;
}

export async function purchase(packageId: string, customerEmail?: string): Promise<{
  revenueCatCustomerId: string;
  isActive: boolean;
}> {
  const pkg = packageCache.get(packageId);
  if (!pkg) throw new Error(`Package not found: ${packageId}`);

  if (__DEV__) {
    console.log('[RC] purchase', packageId, 'customerEmail:', customerEmail ?? '(none — checkout will ask)');
  }

  const purchasePromise = (await getClient()).purchase({
    rcPackage: pkg,
    ...(customerEmail ? { customerEmail } : {}),
  });

  // The checkout's "back to app" arrow closes the SDK UI without settling the
  // purchase promise (observed on 1.26.1), which would leave the paywall
  // spinning forever. Watch the checkout DOM: once it has appeared, it
  // disappearing while the promise is still pending means the user backed out.
  let purchaseSettled = false;
  void purchasePromise.catch(() => {}).finally(() => {
    purchaseSettled = true;
  });

  const dismissalWatcher = new Promise<never>((_resolve, reject) => {
    let checkoutWasVisible = false;
    const interval = setInterval(() => {
      if (purchaseSettled) {
        clearInterval(interval);
        return;
      }
      if (isCheckoutUiMounted()) {
        checkoutWasVisible = true;
        return;
      }
      if (!checkoutWasVisible) return;
      clearInterval(interval);
      setTimeout(() => {
        if (!purchaseSettled) {
          reject(new Error('UserCancelledError: checkout was closed before completing the purchase'));
        }
      }, CHECKOUT_DISMISSAL_GRACE_MS);
    }, CHECKOUT_POLL_INTERVAL_MS);
  });

  const { customerInfo } = await Promise.race([purchasePromise, dismissalWatcher]);
  return {
    revenueCatCustomerId: customerInfo.originalAppUserId,
    isActive: !!customerInfo.entitlements.active[REVENUECAT_ENTITLEMENT_ID],
  };
}

export async function getEntitlementStatus(): Promise<EntitlementStatus> {
  const customerInfo = await getCustomerInfo();
  const entitlement = customerInfo.entitlements.active[REVENUECAT_ENTITLEMENT_ID];
  return {
    isActive: !!entitlement,
    expiresAt: entitlement?.expirationDate?.toISOString() ?? null,
  };
}

export async function getManagementUrl(): Promise<string | null> {
  const customerInfo = await getCustomerInfo();
  return customerInfo.managementURL
    ?? Object.values(customerInfo.subscriptionsByProductIdentifier)
      .find((subscription) => subscription.isActive && subscription.managementURL)?.managementURL
    ?? null;
}

export async function waitForActiveEntitlement(
  options: EntitlementWaitOptions = {},
): Promise<EntitlementStatus> {
  const { attempts = 6, delayMs = 750 } = options;
  let lastStatus: EntitlementStatus = { isActive: false, expiresAt: null };
  let lastError: unknown;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      const status = await getEntitlementStatus();
      lastStatus = status;
      lastError = null;

      if (status.isActive || attempt === attempts - 1) {
        return status;
      }
    } catch (error) {
      lastError = error;
      if (attempt === attempts - 1) {
        throw error;
      }
    }

    await new Promise((resolve) => setTimeout(resolve, delayMs));
  }

  if (lastError) {
    throw lastError;
  }

  return lastStatus;
}

export function addEntitlementStatusListener(
  _listener: (status: EntitlementStatus) => void,
): () => void {
  return () => {};
}

export async function logOutRevenueCat(): Promise<void> {
  // Web SDK does not require explicit logout
}
